import { MCPTool } from '../types';
import { geminiService } from '../../services/geminiService';
import { getMCPServer } from '../server/MCPServer';
import { customerIntelligenceService } from '../../services/customerIntelligenceService';
import { brandBookService } from '../../services/brandBookService';

/**
 * Generates high-quality ad copy or content.
 */
export const generateCopyTool: MCPTool = {
  name: 'generate_copy',
  description: 'Generates high-quality, engaging content such as emails, ad copy, or social media posts.',
  
  inputSchema: {
    type: 'object',
    properties: {
      prompt: {
        type: 'string',
        description: 'The instruction for content generation (e.g., "Write a follow-up email for a car lead")'
      },
      style: {
        type: 'string',
        description: 'Optional style/tone (e.g., "professional", "urgent", "friendly")'
      },
      language: {
        type: 'string',
        description: 'Optional target language code (e.g., "de", "en")'
      }
    },
    required: ['prompt']
  },

  async execute(params) {
    try {
      const fullPrompt = `${params.prompt}${params.style ? ` in ${params.style} style` : ''}${params.language ? ` in ${params.language} language` : ''}`;
      const result = await geminiService.generateWithGemini(fullPrompt, {
        model: 'gemini-3-flash-preview',
        thinkingLevel: 'medium'
      });
      return {
        success: true,
        content: result
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Copy generation failed: ${error.message}`
      };
    }
  }
};

/**
 * Generates professional images.
 */
export const generateImageTool: MCPTool = {
  name: 'generate_image',
  description: 'Generates professional visual assets and images based on a description.',
  
  inputSchema: {
    type: 'object',
    properties: {
      description: {
        type: 'string',
        description: 'The visual description of the image to generate'
      }
    },
    required: ['description']
  },

  async execute(params) {
    try {
      // Note: gemini-3-pro-image-preview is used for image generation
      const result = await geminiService.generateWithGemini(
        `Generate a professional image: ${params.description}`,
        { model: 'gemini-3-pro-image-preview' }
      );
      return {
        success: true,
        imageResult: result,
        message: 'Image generation triggered (Note: returns descriptive result until direct asset handling is implemented)'
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Image generation failed: ${error.message}`
      };
    }
  }
};

/**
 * Generates brand-consistent images using CI/BrandBook context
 */
export const generateBrandImageTool: MCPTool = {
  name: 'generate_brand_image',
  description: `Generates brand-consistent images by automatically fetching visual identity from Customer Intelligence or BrandBook.
Includes brand colors, visual style, and industry-appropriate imagery.
Use this when creating images for a specific brand/customer.`,
  
  inputSchema: {
    type: 'object',
    properties: {
      domain: {
        type: 'string',
        description: 'Domain to get brand context from (e.g., "example.com")'
      },
      imageType: {
        type: 'string',
        enum: ['hero', 'product', 'team', 'banner', 'social', 'ad'],
        description: 'Type of image to generate (default: hero)'
      },
      subject: {
        type: 'string',
        description: 'Main subject of the image (optional, will be inferred from brand if not provided)'
      },
      additionalContext: {
        type: 'string',
        description: 'Additional context or requirements for the image'
      }
    },
    required: ['domain']
  },

  async execute(params, context) {
    try {
      // Step 1: Get brand context from CI or BrandBook
      let brandContext: any = null;
      
      // Try Customer Intelligence first
      const ci = await customerIntelligenceService.findByDomain(context.orgId, params.domain);
      if (ci) {
        brandContext = {
          source: 'customer_intelligence',
          businessName: ci.businessName,
          industry: ci.brand.industry,
          colors: ci.visual?.brandColors || [],
          primaryColor: ci.visual?.primaryColor,
          visualStyle: ci.visual?.visualStyle || 'modern',
          imageStyle: ci.visual?.imageStyle,
          targetAudience: ci.brand.targetAudience
        };
      }
      
      // Try BrandBook as fallback or supplement
      if (!brandContext) {
        const brandBook = await brandBookService.getBrandBookByDomain(context.orgId, params.domain);
        if (brandBook) {
          brandContext = {
            source: 'brand_book',
            businessName: brandBook.businessName,
            industry: brandBook.industry,
            colors: brandBook.colors || [],
            primaryColor: brandBook.colors?.[0],
            visualStyle: brandBook.visualStyle || 'modern',
            imageStyle: brandBook.imageStyle,
            targetAudience: brandBook.targetAudience
          };
        }
      }
      
      if (!brandContext) {
        return {
          success: false,
          error: `No brand context found for ${params.domain}. Create Customer Intelligence or BrandBook first.`
        };
      }

      // Step 2: Build enhanced prompt with brand context
      const imageType = params.imageType || 'hero';
      const subject = params.subject || brandContext.businessName;
      
      const colorDescription = brandContext.colors.length > 0
        ? `Use these brand colors: ${brandContext.colors.slice(0, 3).join(', ')}`
        : brandContext.primaryColor
          ? `Primary brand color: ${brandContext.primaryColor}`
          : 'Use professional, modern colors';
      
      const styleDescription = brandContext.visualStyle 
        ? `Visual style: ${brandContext.visualStyle}`
        : 'Clean, modern, professional';
      
      const industryHints: Record<string, string> = {
        'automotive': 'cars, vehicles, showroom, dealership',
        'ecommerce': 'products, shopping, packages, delivery',
        'saas': 'technology, screens, dashboards, productivity',
        'real estate': 'buildings, homes, interiors, architecture',
        'healthcare': 'medical, wellness, care, professionals',
        'finance': 'business, growth, charts, professional',
        'legal': 'law, justice, office, professional',
        'education': 'learning, books, classroom, students'
      };
      
      const industryContext = industryHints[brandContext.industry.toLowerCase()] || 'professional business context';

      const enhancedPrompt = `Generate a professional ${imageType} image for ${brandContext.businessName} (${brandContext.industry} industry).

BRAND CONTEXT:
- ${colorDescription}
- ${styleDescription}
- Industry visual elements: ${industryContext}
- Target audience: ${brandContext.targetAudience || 'professionals'}

IMAGE REQUIREMENTS:
- Type: ${imageType}
- Main subject: ${subject}
${params.additionalContext ? `- Additional: ${params.additionalContext}` : ''}

Create a high-quality, brand-consistent image that reflects the company's identity and appeals to their target audience.`;

      // Step 3: Generate the image
      const result = await geminiService.generateWithGemini(
        enhancedPrompt,
        { model: 'gemini-3-pro-image-preview' }
      );

      return {
        success: true,
        imageResult: result,
        brandContext: {
          source: brandContext.source,
          businessName: brandContext.businessName,
          industry: brandContext.industry,
          colorsUsed: brandContext.colors.slice(0, 3),
          visualStyle: brandContext.visualStyle
        },
        promptUsed: enhancedPrompt,
        message: `Brand-consistent ${imageType} image generated for ${brandContext.businessName}`
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Brand image generation failed: ${error.message}`
      };
    }
  }
};

/**
 * Generates brand-consistent ad copy using CI/BrandBook voice context
 */
export const generateBrandCopyTool: MCPTool = {
  name: 'generate_brand_copy',
  description: `Generates brand-consistent ad copy by automatically fetching voice guidelines from Customer Intelligence or BrandBook.
Respects address form (Sie/Du), tone, and key messages.
Use this when creating copy for a specific brand/customer.`,
  
  inputSchema: {
    type: 'object',
    properties: {
      domain: {
        type: 'string',
        description: 'Domain to get brand context from'
      },
      copyType: {
        type: 'string',
        enum: ['headline', 'description', 'email', 'social', 'cta'],
        description: 'Type of copy to generate'
      },
      purpose: {
        type: 'string',
        description: 'Purpose/goal of the copy (e.g., "promote summer sale", "announce new product")'
      },
      count: {
        type: 'number',
        description: 'Number of variations to generate (default: 5)'
      }
    },
    required: ['domain', 'copyType', 'purpose']
  },

  async execute(params, context) {
    try {
      // Get voice context from CI or BrandBook
      let voiceContext: any = null;
      
      const ci = await customerIntelligenceService.findByDomain(context.orgId, params.domain);
      if (ci?.voice) {
        voiceContext = {
          source: 'customer_intelligence',
          addressForm: ci.voice.addressForm || 'Sie',
          formality: ci.voice.formality || 'formal',
          toneAttributes: ci.voice.toneAttributes || [],
          keyMessages: ci.voice.keyMessages || [],
          doStatements: ci.voice.doStatements || [],
          dontStatements: ci.voice.dontStatements || []
        };
      }
      
      if (!voiceContext) {
        const brandBook = await brandBookService.getBrandBookByDomain(context.orgId, params.domain);
        if (brandBook?.voice) {
          voiceContext = {
            source: 'brand_book',
            addressForm: brandBook.voice.addressForm || 'Sie',
            formality: brandBook.voice.formality || 'formal',
            toneAttributes: brandBook.voice.toneAttributes || [],
            keyMessages: brandBook.voice.keyMessages || [],
            doStatements: brandBook.voice.doStatements || [],
            dontStatements: brandBook.voice.dontStatements || []
          };
        }
      }
      
      if (!voiceContext) {
        // Use default professional voice
        voiceContext = {
          source: 'default',
          addressForm: 'Sie',
          formality: 'formal',
          toneAttributes: ['professional', 'trustworthy'],
          keyMessages: [],
          doStatements: [],
          dontStatements: []
        };
      }

      // Build prompt with voice guidelines
      const count = params.count || 5;
      const copyType = params.copyType;
      
      const lengthGuidelines: Record<string, string> = {
        'headline': 'Max 30 characters per headline',
        'description': 'Max 90 characters per description',
        'email': '2-3 paragraphs',
        'social': 'Max 280 characters',
        'cta': 'Max 20 characters, action-oriented'
      };

      const prompt = `Generate ${count} ${copyType} variations for: ${params.purpose}

VOICE GUIDELINES (CRITICAL - MUST FOLLOW):
- Address form: ${voiceContext.addressForm} (use this pronoun!)
- Formality: ${voiceContext.formality}
- Tone: ${voiceContext.toneAttributes.join(', ') || 'professional'}
${voiceContext.keyMessages.length > 0 ? `- Key messages to incorporate: ${voiceContext.keyMessages.join('; ')}` : ''}
${voiceContext.doStatements.length > 0 ? `- DO: ${voiceContext.doStatements.join('; ')}` : ''}
${voiceContext.dontStatements.length > 0 ? `- DON'T: ${voiceContext.dontStatements.join('; ')}` : ''}

FORMAT REQUIREMENTS:
- Type: ${copyType}
- ${lengthGuidelines[copyType] || ''}
- Language: German

Return as JSON array: { "variations": ["text1", "text2", ...] }`;

      const result = await geminiService.generateJSON(prompt);

      return {
        success: true,
        copyType: params.copyType,
        purpose: params.purpose,
        variations: result.variations || [],
        voiceContext: {
          source: voiceContext.source,
          addressForm: voiceContext.addressForm,
          formality: voiceContext.formality,
          toneApplied: voiceContext.toneAttributes
        },
        message: `Generated ${result.variations?.length || 0} ${copyType} variations using ${voiceContext.addressForm} form`
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Brand copy generation failed: ${error.message}`
      };
    }
  }
};

export function registerCreativeTools(): void {
  const server = getMCPServer();
  
  server.registerTool(generateCopyTool, {
    category: 'creative',
    requiresAuth: true,
    dangerLevel: 'safe'
  });
  
  server.registerTool(generateImageTool, {
    category: 'creative',
    requiresAuth: true,
    dangerLevel: 'safe'
  });
  
  server.registerTool(generateBrandImageTool, {
    category: 'creative',
    requiresAuth: true,
    dangerLevel: 'safe'
  });
  
  server.registerTool(generateBrandCopyTool, {
    category: 'creative',
    requiresAuth: true,
    dangerLevel: 'safe'
  });
  
  console.log('✅ Registered 4 creative tools');
}

